import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DeviceDto } from '@gc/shared';

import { api, ApiError } from '@/lib/api';

/**
 * Confirm-then-DELETE for one device (wave 3 #5). The route
 * (`DELETE /devices/:id`, `devices.manage`) has existed since wave 1 with no
 * UI. Server refusals surface by code — `device_in_use` gets its own message,
 * because "close the running session first" is an instruction, not an error.
 */
export function DeleteDeviceDialog({
  device,
  onClose,
  onDeleted,
}: {
  device: DeviceDto;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleDelete(): Promise<void> {
    setError(null);
    setSubmitting(true);
    try {
      await api.delete(`/devices/${device.id}`);
      onDeleted();
      onClose();
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'device_in_use') {
        setError(t('deviceBoard.deleteDialog.inUse', { device: device.name }));
      } else {
        setError(
          caught instanceof ApiError ? caught.message : t('deviceBoard.deleteDialog.failed'),
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('deviceBoard.deleteDialog.title', { device: device.name })}
    >
      <div className="w-full max-w-sm rounded-card border border-subtle bg-raised p-5 shadow-2xl">
        <h2 className="text-lg font-semibold text-content">
          {t('deviceBoard.deleteDialog.title', { device: device.name })}
        </h2>
        <p className="mt-2 text-sm text-content-secondary">
          {t('deviceBoard.deleteDialog.message')}
        </p>

        {error ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void handleDelete()}
            disabled={submitting}
            className="rounded-full bg-danger px-4 py-2 text-sm font-medium text-white hover:bg-danger-hover disabled:opacity-60"
          >
            {submitting ? t('common.loading') : t('common.delete')}
          </button>
        </div>
      </div>
    </div>
  );
}
